import { jobRoleLabel } from './jobRoles';

// Pure view logic for Settings -> Users, so every filter, sort and sentence is
// asserted without jsdom.

export type UserRow = {
  uid: string;
  email: string;
  name: string;
  photoURL: string | null;
  jobRole: string | null;
  role: 'admin' | 'staff';
  lastSignedInAt: string | null;
  disabledAt: string | null;
  accountsManaged: number;
};

export type UserFilters = {
  search: string;
  /** '' = any; 'none' = no role yet; otherwise a job role key. */
  jobRole: string;
  access: '' | 'admin' | 'staff';
  status: '' | 'active' | 'disabled';
};

export const NO_FILTERS: UserFilters = { search: '', jobRole: '', access: '', status: '' };

export function filterUsers(rows: UserRow[], f: UserFilters): UserRow[] {
  const q = f.search.trim().toLowerCase();
  return rows.filter((r) => {
    if (q && !r.name.toLowerCase().includes(q) && !r.email.toLowerCase().includes(q)) return false;
    if (f.jobRole === 'none' && r.jobRole) return false;
    if (f.jobRole && f.jobRole !== 'none' && r.jobRole !== f.jobRole) return false;
    if (f.access && r.role !== f.access) return false;
    if (f.status === 'active' && r.disabledAt) return false;
    if (f.status === 'disabled' && !r.disabledAt) return false;
    return true;
  });
}

export type UserSortKey = 'name' | 'lastSignedIn' | 'role';

/** Stable; name is the tiebreak for every key. Never signed in sorts last
 *  ascending-by-recency (i.e. "most recent first" puts them at the end). */
export function sortUsers(rows: UserRow[], key: UserSortKey, dir: 'asc' | 'desc'): UserRow[] {
  const sign = dir === 'asc' ? 1 : -1;
  const byName = (a: UserRow, b: UserRow) => a.name.localeCompare(b.name);
  return [...rows].sort((a, b) => {
    let c = 0;
    if (key === 'lastSignedIn') {
      const am = a.lastSignedInAt ? Date.parse(a.lastSignedInAt) : null;
      const bm = b.lastSignedInAt ? Date.parse(b.lastSignedInAt) : null;
      if (am === null && bm === null) c = 0;
      else if (am === null) return 1; // never signed in: always last
      else if (bm === null) return -1;
      else c = am - bm;
    } else if (key === 'role') {
      c = jobRoleLabel(a.jobRole).localeCompare(jobRoleLabel(b.jobRole));
    } else {
      c = byName(a, b);
    }
    return c !== 0 ? c * sign : byName(a, b);
  });
}

export function formatLastSignedIn(iso: string | null, nowMs: number): string {
  if (!iso) return 'Never';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 'Never';
  const s = Math.max(0, Math.round((nowMs - t) / 1000));
  if (s < 60) return 'Just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d} day${d === 1 ? '' : 's'} ago`;
  const mo = Math.round(d / 30);
  if (mo < 12) return `${mo} month${mo === 1 ? '' : 's'} ago`;
  const y = Math.round(mo / 12);
  return `${y} year${y === 1 ? '' : 's'} ago`;
}

/** The warning shown on a disabled user who still manages accounts. */
export function stillManagesWarning(row: Pick<UserRow, 'disabledAt' | 'accountsManaged'>): string | null {
  if (!row.disabledAt || row.accountsManaged <= 0) return null;
  const n = row.accountsManaged;
  return `Still the account manager for ${n} account${n === 1 ? '' : 's'}`;
}

/** What the confirm dialog says. Names the person and what happens. */
export function disableConfirmText(name: string, accountsManaged: number): string {
  const base =
    `${name} will be signed out and refused on their next request until an admin turns them back on. ` +
    'Nothing is deleted, and their name stays on old records.';
  return accountsManaged > 0
    ? `${base} They are still the account manager for ${accountsManaged} account${accountsManaged === 1 ? '' : 's'}; those keep them as manager until reassigned.`
    : base;
}
